'use client';

import { MouseEvent } from 'react';

type BlogSectionAnchorLink = {
  title: string;
  anchorId: string;
};

type Props = {
  links: BlogSectionAnchorLink[];
  title: string;
};

const SCROLL_OFFSET_BUFFER = 24;

function getScrollOffset() {
  const header = document.querySelector('header');

  if (!header) {
    return 112;
  }

  return header.getBoundingClientRect().height + SCROLL_OFFSET_BUFFER;
}

function scrollToSection(anchorId: string) {
  const element = document.getElementById(anchorId);

  if (!element) return;

  const top =
    element.getBoundingClientRect().top + window.scrollY - getScrollOffset();

  window.scrollTo({ top, behavior: 'smooth' });
  window.history.pushState(null, '', `#${anchorId}`);
}

function AnchorLinks({ links }: { links: BlogSectionAnchorLink[] }) {
  const handleAnchorClick = (
    event: MouseEvent<HTMLAnchorElement>,
    anchorId: string,
  ) => {
    event.preventDefault();
    scrollToSection(anchorId);
  };

  return (
    <>
      {links.map(({ title, anchorId }) => (
        <li key={anchorId} className="mb-2">
          <a
            href={`#${anchorId}`}
            onClick={(event) => handleAnchorClick(event, anchorId)}
            className="bg-gradient-to-b from-underline to-underline bg-[length:95%_3px] bg-bottom bg-no-repeat py-0 transition-all duration-500 ease-in-out hover:bg-[length:95%_15px]">
            {title}
          </a>
        </li>
      ))}
    </>
  );
}

export default function BlogArticleSectionAnchors({ links, title }: Props) {
  if (links.length === 0) return null;

  const firstColumnLinkCount = Math.ceil(links.length / 2);
  const firstColumnLinks = links.slice(0, firstColumnLinkCount);
  const secondColumnLinks = links.slice(firstColumnLinkCount);

  return (
    <nav
      aria-label="Article sections"
      className="mx-auto  mb-[80px] w-full max-w-default px-sm md:px-md desktop:px-lg">
      <div className="w-full desktop:w-fit desktop:mx-auto pb-6 border-b desktop:border-t desktop:py-10 border-gray-300">
        <div className="desktop:hidden">
          <h2 className="mb-4 text-3xl font-title leading-[1.25] laptop:text-4xl">
            {title}
          </h2>
          <ul className="list-disc pl-5">
            <AnchorLinks links={links} />
          </ul>
        </div>

        <div className="mx-auto hidden max-w-[60rem] desktop:grid desktop:grid-cols-2 desktop:gap-x-12">
          <h2 className="mb-4 text-3xl font-title leading-[1.25] laptop:text-4xl">
            {title}
          </h2>
          <div aria-hidden="true" />
          <ul className="list-disc pl-5">
            <AnchorLinks links={firstColumnLinks} />
          </ul>
          {secondColumnLinks.length > 0 && (
            <ul className="list-disc pl-5">
              <AnchorLinks links={secondColumnLinks} />
            </ul>
          )}
        </div>
      </div>
    </nav>
  );
}
